import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import { clearPortalPinChallenge } from "@/lib/activeLoginSession";

interface FactoryData {
  id: string;
  factory_name: string;
  factory_code?: string;
  address?: string;
  tenant_id?: string;
}

interface FactoryContext {
  factoryId: string;
  factoryData: FactoryData;
}

interface FactoryLoginContextType {
  factoryContext: FactoryContext | null;
  factoryContextLoading: boolean;
  selectFactory: (factoryId: string, factoryData: FactoryData) => void;
  clearFactoryContext: () => void;
  isFactorySelected: boolean;
}

const STORAGE_KEY = 'lysara_factory_login';

const FactoryLoginContext = createContext<FactoryLoginContextType>({
  factoryContext: null,
  factoryContextLoading: true,
  selectFactory: () => {},
  clearFactoryContext: () => {},
  isFactorySelected: false
});

export const FactoryLoginProvider = ({ children }: { children: ReactNode }) => {
  const [factoryContext, setFactoryContext] = useState<FactoryContext | null>(null);
  const [factoryContextLoading, setFactoryContextLoading] = useState(true);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Validate: factoryId must exist
        if (!parsed.factoryId) {
          console.warn('Invalid factory context in localStorage (missing factoryId), clearing...');
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
        
        // Auto-heal: if factoryData.id is missing but factoryId exists, populate it
        if (parsed.factoryData && !parsed.factoryData.id) {
          parsed.factoryData.id = parsed.factoryId;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
        
        setFactoryContext(parsed);
      }
    } catch (error) {
      console.error('Failed to load factory context from localStorage:', error);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setFactoryContextLoading(false);
    }
  }, []);

  const selectFactory = (factoryId: string, factoryData: FactoryData) => {
    const context: FactoryContext = { factoryId, factoryData };
    setFactoryContext(context);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  };

  const clearFactoryContext = () => {
    setFactoryContext(null);
    localStorage.removeItem(STORAGE_KEY);
    clearPortalPinChallenge("factory");
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    factoryContext,
    factoryContextLoading,
    selectFactory,
    clearFactoryContext,
    isFactorySelected: !!factoryContext
  }), [factoryContext, factoryContextLoading]);

  return (
    <FactoryLoginContext.Provider value={contextValue}>
      {children}
    </FactoryLoginContext.Provider>
  );
};

export const useFactoryLogin = () => useContext(FactoryLoginContext);
